import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '@/components/Select';
import { CountryMultiSelect } from '@/components/CountryMultiSelect';
import { getTrackerStats } from '@/api/tracker-stats';
import { useAuthStore } from '@/store/auth';
import { getCampaigns } from '@/api/tracker-campaigns';
import { getOffers } from '@/api/offers';
import { formatOfferLabel } from '@/lib/offers';
import type { StatsGroup } from '@/api/tracker-types';

function fmtCents(c: number) {
  return `$${(c / 100).toFixed(2)}`;
}
function pct(num: number, denom: number) {
  if (!denom) return '0.00%';
  return `${(num / denom * 100).toFixed(2)}%`;
}

const GROUP_OPTIONS = [
  { value: 'campaign', label: 'Campaign' },
  { value: 'flow', label: 'Flow' },
  { value: 'offer', label: 'Offer' },
  { value: 'partner', label: 'Advertiser (partner)' },
  { value: 'country', label: 'Country' },
  { value: 'offer_country', label: 'Offer × Country' },
  { value: 'partner_country', label: 'Advertiser × Country' },
  { value: 'day', label: 'Day' },
  { value: 'source', label: 'Source type' },
  { value: 'source_bundle', label: 'Source bundle' },
  { value: 'source_app', label: 'Source app' },
  { value: 'source_ad', label: 'Source ad' },
  { value: 'source_campaign', label: 'Source campaign' },
];

// Quick toggle over the two breakdowns that get looked at most. It drives the
// same `group` state as the Group by dropdown, so the two stay in sync; picking
// any other grouping simply leaves both segments inactive.
const BREAKDOWN_TABS: { value: StatsGroup; label: string }[] = [
  { value: 'campaign', label: 'Campaigns' },
  { value: 'country', label: 'Geo' },
];

// Header of the first table column. Composite groups name only their first half
// here — the second gets its own header (groupHeader2).
const GROUP_HEADERS: Record<string, string> = {
  offer_country: 'Offer',
  partner_country: 'Advertiser',
};

const SOURCE_TYPE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'organic', label: 'organic' },
  { value: 'organic_force', label: 'organic_force' },
  { value: 'referral_force', label: 'referral_force' },
  { value: 'referral_click', label: 'referral_click' },
  { value: 'ad_click', label: 'ad_click' },
  { value: 'playable', label: 'playable' },
];

// Groupings by our SDK traffic sources; a client's campaigns get none of it
// and the API refuses them for clients.
const CLIENT_CLOSED_GROUPS = new Set(['source_bundle', 'source_app', 'source_ad', 'source_campaign']);

export function Tracker() {
  const isClient = useAuthStore((s) => s.isClient());
  const groupOptions = isClient ? GROUP_OPTIONS.filter((o) => !CLIENT_CLOSED_GROUPS.has(o.value)) : GROUP_OPTIONS;
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(weekAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [group, setGroup] = useState<StatsGroup>('campaign');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [labelFilter, setLabelFilter] = useState('');
  const [offerFilter, setOfferFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [sourceType, setSourceType] = useState('');
  const [sourceBundleId, setSourceBundleId] = useState('');

  const { data: campaigns = [] } = useQuery({ queryKey: ['tracker-campaigns'], queryFn: getCampaigns });
  const { data: offers = [] } = useQuery({ queryKey: ['offers'], queryFn: getOffers });
  const campaignName = (id: string | null) => {
    if (!id) return '-';
    const c = campaigns.find((x) => x.id === id);
    if (c) return c.name;
    return `(deleted) ${id.slice(0, 8)}…`;
  };
  const offerLabel = (id: string | null) => {
    if (!id) return '-';
    const o = offers.find((x) => x.id === id);
    return o ? formatOfferLabel(o) : id;
  };

  type SortKey = 'group' | 'group2' | 'clicks' | 'unique' | 'regs' | 'cr_reg' | 'deps' | 'cr_dep' | 'payout' | 'epc';
  const [sortKey, setSortKey] = useState<SortKey>('clicks');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'group' || k === 'group2' ? 'asc' : 'desc'); }
  };

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['tracker-stats', from, to, group, campaignFilter, labelFilter, offerFilter, partnerFilter, countries, sourceType, sourceBundleId],
    queryFn: () => getTrackerStats({
      from: from ? `${from} 00:00:00` : undefined,
      to: to ? `${to} 23:59:59` : undefined,
      group_by: group,
      campaign_id: campaignFilter || undefined,
      label: labelFilter || undefined,
      offer_id: offerFilter || undefined,
      partner: partnerFilter || undefined,
      countries: countries.length ? countries : undefined,
      source_type: sourceType || undefined,
      source_bundle_id: sourceBundleId.trim() || undefined,
    }),
  });

  const totals = rows.reduce((acc, r) => ({
    clicks: acc.clicks + r.clicks,
    unique_clicks: acc.unique_clicks + r.unique_clicks,
    regs: acc.regs + r.regs,
    deps: acc.deps + r.deps,
    payout_cents: acc.payout_cents + r.payout_cents,
  }), { clicks: 0, unique_clicks: 0, regs: 0, deps: 0, payout_cents: 0 });

  function labelOf(value: string | null): string {
    if (!value) return '-';
    if (group === 'campaign') return campaignName(value);
    if (group === 'offer' || group === 'offer_country') return offerLabel(value);
    return value;
  }

  const labelValues = Array.from(new Set(campaigns.map((c) => c.label).filter((l): l is string => !!l))).sort();
  const labelOptions = [{ value: '', label: 'All labels' }, ...labelValues.map((l) => ({ value: l, label: l }))];
  const campaignOptions = [
    { value: '', label: 'All campaigns' },
    ...campaigns
      .filter((c) => !labelFilter || c.label === labelFilter)
      .map((c) => ({ value: c.id, label: c.name })),
  ];

  const offersForPartner = partnerFilter
    ? offers.filter((o) => o.partner === partnerFilter)
    : offers;

  const offerOptions = [
    { value: '', label: 'All offers' },
    ...offersForPartner.map((o) => ({ value: o.id, label: formatOfferLabel(o) })),
  ];

  const partnerOptions = [
    { value: '', label: 'All advertisers' },
    ...Array.from(new Set(offers.map((o) => o.partner))).sort()
      .map((p) => ({ value: p, label: p })),
  ];

  const handlePartnerChange = (p: string) => {
    setPartnerFilter(p);
    if (offerFilter) {
      const o = offers.find((x) => x.id === offerFilter);
      if (p && (!o || o.partner !== p)) setOfferFilter('');
    }
  };

  const isComposite = group === 'offer_country' || group === 'partner_country';

  const sortedRows = [...rows].sort((a, b) => {
    const epc = (r: typeof a) => (r.clicks ? r.payout_cents / r.clicks : 0);
    const cr = (n: number, d: number) => (d ? n / d : 0);
    let av: number | string = 0;
    let bv: number | string = 0;
    switch (sortKey) {
      case 'group': av = labelOf(a.group_value); bv = labelOf(b.group_value); break;
      case 'group2': av = a.group_value_2 || ''; bv = b.group_value_2 || ''; break;
      case 'clicks': av = a.clicks; bv = b.clicks; break;
      case 'unique': av = a.unique_clicks; bv = b.unique_clicks; break;
      case 'regs': av = a.regs; bv = b.regs; break;
      case 'cr_reg': av = cr(a.regs, a.clicks); bv = cr(b.regs, b.clicks); break;
      case 'deps': av = a.deps; bv = b.deps; break;
      case 'cr_dep': av = cr(a.deps, a.clicks); bv = cr(b.deps, b.clicks); break;
      case 'payout': av = a.payout_cents; bv = b.payout_cents; break;
      case 'epc': av = epc(a); bv = epc(b); break;
    }
    const cmp = typeof av === 'string' && typeof bv === 'string'
      ? av.localeCompare(bv)
      : (av as number) - (bv as number);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const arrow = (k: SortKey) => sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const thBase = 'px-4 py-2 text-xs font-medium text-content-subtle uppercase cursor-pointer select-none hover:text-content-muted';
  const thL = `${thBase} text-left`;
  const thR = `${thBase} text-right`;
  const groupHeader2 = group === 'offer_country' ? 'Country'
    : group === 'partner_country' ? 'Country'
    : null;
  const groupHeader = GROUP_HEADERS[group]
    ?? GROUP_OPTIONS.find((o) => o.value === group)?.label
    ?? group;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Tracker</h1>
        <p className="text-sm text-content-subtle mt-1">Click and conversion analytics</p>
      </div>

      <div className="bg-surface border border-border rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-content-subtle mb-1">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-3 py-2 border border-border rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-3 py-2 border border-border rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Group by</label>
          <Select value={group} options={groupOptions} onChange={(v) => setGroup(v as StatsGroup)} />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Label</label>
          <Select
            value={labelFilter}
            options={labelOptions}
            onChange={(v) => {
              setLabelFilter(v);
              if (v && campaignFilter) {
                const c = campaigns.find((x) => x.id === campaignFilter);
                if (!c || c.label !== v) setCampaignFilter('');
              }
            }}
            searchable
          />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Campaign filter</label>
          <Select value={campaignFilter} options={campaignOptions} onChange={setCampaignFilter} searchable />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Advertiser</label>
          <Select value={partnerFilter} options={partnerOptions} onChange={handlePartnerChange} searchable />
        </div>
        <div className="min-w-[20rem]">
          <label className="block text-xs text-content-subtle mb-1">Offer</label>
          <Select value={offerFilter} options={offerOptions} onChange={setOfferFilter} searchable />
        </div>
        <div className="min-w-[16rem] flex-1 max-w-md">
          <label className="block text-xs text-content-subtle mb-1">Countries</label>
          <CountryMultiSelect value={countries} onChange={setCountries} placeholder="All countries" size="sm" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Source type</label>
          <Select value={sourceType} options={SOURCE_TYPE_OPTIONS} onChange={setSourceType} />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">Source bundle</label>
          <input
            type="text" value={sourceBundleId} onChange={(e) => setSourceBundleId(e.target.value)}
            placeholder="com.example.app"
            className="px-3 py-2 border border-border rounded-lg text-sm font-mono"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <Total label="Clicks" value={totals.clicks.toString()} />
        <Total label="Unique" value={totals.unique_clicks.toString()} />
        <Total label="Regs" value={`${totals.regs} (${pct(totals.regs, totals.clicks)})`} />
        <Total label="Deps" value={`${totals.deps} (${pct(totals.deps, totals.clicks)})`} />
        <Total label="Payout" value={fmtCents(totals.payout_cents)} />
      </div>

      <div className="bg-surface border border-border rounded-lg">
        <div className="px-4 py-3 border-b border-border">
          <div className="inline-flex bg-surface-muted rounded-lg p-0.5 gap-0.5">
            {BREAKDOWN_TABS.map((t) => (
              <button
                key={t.value}
                onClick={() => setGroup(t.value)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  group === t.value ? 'bg-surface text-content shadow-sm' : 'text-content-subtle hover:text-content-muted'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-surface-muted">
            <tr>
              <th className={thL} onClick={() => toggleSort('group')}>{groupHeader}{arrow('group')}</th>
              {groupHeader2 && (
                <th className={thL} onClick={() => toggleSort('group2')}>{groupHeader2}{arrow('group2')}</th>
              )}
              <th className={thR} onClick={() => toggleSort('clicks')}>Clicks{arrow('clicks')}</th>
              <th className={thR} onClick={() => toggleSort('unique')}>Unique{arrow('unique')}</th>
              <th className={thR}>
                <span onClick={() => toggleSort('regs')}>Regs{arrow('regs')}</span>
                <span className="text-content-subtle"> / </span>
                <span onClick={() => toggleSort('cr_reg')}>CR{arrow('cr_reg')}</span>
              </th>
              <th className={thR}>
                <span onClick={() => toggleSort('deps')}>Deps{arrow('deps')}</span>
                <span className="text-content-subtle"> / </span>
                <span onClick={() => toggleSort('cr_dep')}>CR{arrow('cr_dep')}</span>
              </th>
              <th className={thR} onClick={() => toggleSort('payout')}>Payout{arrow('payout')}</th>
              <th className={thR} onClick={() => toggleSort('epc')}>EPC{arrow('epc')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border text-sm">
            {isLoading && <tr><td colSpan={isComposite ? 8 : 7} className="text-center py-8 text-content-subtle">Loading…</td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={isComposite ? 8 : 7} className="text-center py-8 text-content-subtle">No data</td></tr>}
            {sortedRows.map((r, i) => (
              <tr key={i}>
                <td className="px-4 py-2">{labelOf(r.group_value)}</td>
                {isComposite && <td className="px-4 py-2">{r.group_value_2 || '-'}</td>}
                <td className="px-4 py-2 text-right">{r.clicks}</td>
                <td className="px-4 py-2 text-right">{r.unique_clicks}</td>
                <td className="px-4 py-2 text-right">{r.regs} <span className="text-xs text-content-subtle">({pct(r.regs, r.clicks)})</span></td>
                <td className="px-4 py-2 text-right">{r.deps} <span className="text-xs text-content-subtle">({pct(r.deps, r.clicks)})</span></td>
                <td className="px-4 py-2 text-right">{fmtCents(r.payout_cents)}</td>
                <td className="px-4 py-2 text-right">{r.clicks ? `$${(r.payout_cents / 100 / r.clicks).toFixed(3)}` : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function Total({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface border border-border rounded-lg p-3">
      <div className="text-xs text-content-subtle">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
